import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Moon, Hand, MessageCircle, Hash, Star, Heart } from "lucide-react";

const FEATURES = [
  { icon: Moon, title: "Vedic Birth Chart (Rashi, Navamsa, Dasamsa + 3 more Vargas)", desc: "Lahiri sidereal computations powered by Swiss Ephemeris." },
  { icon: Sparkles, title: "Vimshottari Dasha Depth", desc: "Mahadasha › Antardasha › Pratyantar timelines with life-event predictions." },
  { icon: Star, title: "Yogas Detection", desc: "Raj Yoga, Dhan Yoga, Panch Mahapurush, Gaja Kesari, Neecha Bhanga — each with simple meanings." },
  { icon: Hash, title: "Numerology Suite", desc: "Driver / Conductor numbers, Lo Shu grid, Name Numerology, lucky number predictions." },
  { icon: Heart, title: "Match-Making (Guna Milan)", desc: "8-koot compatibility with AI-written insight into the match." },
  { icon: Hand, title: "AI Palm Reading", desc: "Vedic Samudrik Shastra analysis of your palm photograph." },
  { icon: MessageCircle, title: "AI Astro Talk", desc: "Chat with an AI astrologer grounded in your real birth chart — remembers past sessions." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 starfield" data-testid="about-page">
      <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-serif text-lg text-slate-50">Cosmic Sage</span>
          </Link>
          <Link to="/dashboard" className="text-sm text-amber-300 hover:text-amber-200">← Back to App</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-serif text-5xl text-slate-50 mb-3">About Cosmic Sage</h1>
        <p className="text-slate-400 text-lg mb-10">
          Where ancient Vedic wisdom meets modern AI — crafted for seekers, by builders.
        </p>

        <section className="space-y-5 text-slate-300 leading-relaxed">
          <p>
            <b className="text-amber-300">Cosmic Sage</b> is a free Vedic astrology and numerology
            platform that computes authentic Lahiri-sidereal charts, generates deep AI-powered
            interpretations, and makes classical Jyotish accessible to seekers around the world —
            with no account required to get started.
          </p>
          <p>
            Our mission is simple: <i>take the beauty, depth and specificity of classical Vedic
            astrology and present it in a way anyone — not just advanced students — can actually
            use.</i> Every reading, every yoga, every dasha prediction on this site is grounded in
            real Parashari rules, computed from your actual birth-time coordinates using the Swiss
            Ephemeris library trusted by professional astrologers worldwide.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-serif text-3xl text-slate-50 mb-6">What You Get</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="p-5 rounded-2xl bg-slate-900/50 border border-white/5" data-testid={`about-feature-${i}`}>
                <f.icon className="w-6 h-6 text-amber-400 mb-3" />
                <div className="font-serif text-lg text-slate-50">{f.title}</div>
                <div className="text-sm text-slate-400 mt-1">{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 space-y-5 text-slate-300 leading-relaxed">
          <h2 className="font-serif text-3xl text-slate-50 mb-3">How It Works</h2>
          <p>
            Cosmic Sage is free thanks to a small ad-reward loop. You get <b>3 free premium
            readings</b> on arrival. When you need more, watch a 5-second sponsored video and your
            quota resets. Simple, fair, and lets us keep every feature free for everyone.
          </p>
          <p>
            Our AI features are powered by Anthropic's Claude Sonnet with a Vedic-specific system
            prompt. We never store your palm photographs — they're analysed in-memory and discarded.
          </p>
        </section>

        <section className="mt-10 space-y-5 text-slate-300 leading-relaxed">
          <h2 className="font-serif text-3xl text-slate-50 mb-3">A Note on Astrology</h2>
          <p>
            Jyotish is a 5,000-year-old science of light. We treat it with respect — not as fortune-
            telling, but as a mirror that lets you examine your tendencies, timings and karmic
            themes. Readings are for self-reflection and entertainment; they are never a substitute
            for medical, legal, financial, or relationship advice from qualified professionals.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-serif text-3xl text-slate-50 mb-3">Contact</h2>
          <p className="text-slate-300">
            Questions, feedback, or want to collaborate? Write to us at{" "}
            <a href="mailto:support@cosomicsage.com" className="text-amber-300 underline">support@cosomicsage.com</a>.
          </p>
        </section>

        <footer className="mt-12 pt-6 border-t border-white/5 text-sm text-slate-500 flex flex-wrap gap-4">
          <Link to="/" className="hover:text-amber-300">Home</Link>
          <Link to="/privacy" className="hover:text-amber-300">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-amber-300">Terms of Service</Link>
        </footer>
      </main>
    </div>
  );
}
